import { isEmpty, isNil, last } from 'lodash';
import { useEffect } from 'react';
import { batch } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { updateAuthProgress } from '../../redux/actions/authProgress';
import {
  enableKnownUserSignup,
  setShowSubscriptionCodeField,
  updateBillingInfo,
} from '../../redux/actions/billing';
import { setAuthMessage } from '../../redux/actions/common';
import { setDeviceAuthCode } from '../../redux/actions/device-auth';
import { ErrorType } from '../../types/analytics';
import { COMPONENT_FLASH_BANNER } from '../../utils/components-elements';
import { CATCH_ERROR } from '../../utils/defaults';
import { COUPON_REDIRECT_MESSAGE, INVALID_OFFER_FLASH_TEXT } from '../../utils/notices';
import { isSignupPath } from '../../utils/router';
import { NOTICE_ERROR_TYPE, showFlashNotice } from '../../web-shared/redux/actions/notices';
import {
  flashSubscriptionCodeStatus,
  setAddOnPriceChangeConfig,
  setSharedBillingInfo,
  setSubscriptionCodeValidationError,
  validateSubscriptionCode,
} from '../../web-shared/redux/actions/shared-billing';
import { getSubscriptionCodeValid } from '../../web-shared/redux/selectors/shared-billing';
import { CouponItem, OfferType } from '../../web-shared/types/billing';
import useErrorTrack from '../analytics/use-error-track';
import useLoadUserGeolocation from '../auth/use-load-user-geolocation';
import useStartSignup from '../billing/use-start-signup';
import { useDeviceActivationCode } from '../router/use-device-activation-code';
import useQueryParams from '../router/use-query-params';
import useAppStore from '../use-app-store';

/** KU
 *
 * This app-wide hook performs various redux actions that a required for one the
 * app first hydrates. These actions include verifying the user's geolocation,
 * validating any linked coupon codes, and performing initial in-app redirects.
 */
export default function useAppRehydrate() {
  const { dispatch, getState } = useAppStore();
  const { handleStartSignup } = useStartSignup();
  const { handleLoadUserGeolocation } = useLoadUserGeolocation();
  const { handleErrorDisplayedTrack } = useErrorTrack();

  const query = useQueryParams();
  const deviceAuthCode = useDeviceActivationCode();

  const { pathname } = useLocation();

  useEffect(() => {
    const state = getState();
    const { config } = state;

    const { couponList = [], showCouponPrompt } = config;
    const mainCouponDetails: Partial<CouponItem & { errorMessage?: string; terms?: string }> =
      last(couponList) || {};
    const {
      token: couponToken,
      errorMessage: couponErrorMessage,
      terms,
      terms_url: termsUrl,
    } = mainCouponDetails;

    dispatch(setShowSubscriptionCodeField(!!showCouponPrompt));

    const { code, showForm } = query;
    if (code === '' && showCouponPrompt) {
      dispatch(showFlashNotice({ body: INVALID_OFFER_FLASH_TEXT, type: NOTICE_ERROR_TYPE }));
      handleErrorDisplayedTrack({
        message: INVALID_OFFER_FLASH_TEXT,
        errorType: ErrorType.MODAL,
        component: COMPONENT_FLASH_BANNER,
        canRetry: false,
      });
    }
    if (code) {
      dispatch(validateSubscriptionCode(code))
        .then(() => {
          const subscriptionCodeValid = getSubscriptionCodeValid(getState());
          dispatch(
            updateBillingInfo({
              subscriptionCodeInput: code,
              isLinkedOffer: true,
              showSubscriptionCodeField: true,
            }),
          );
          if (subscriptionCodeValid) {
            dispatch(flashSubscriptionCodeStatus());
            handleStartSignup();
          }
        })
        .catch(CATCH_ERROR);
    } else if (showForm) {
      handleStartSignup();
    }

    if (deviceAuthCode) {
      dispatch(setDeviceAuthCode(deviceAuthCode));
    }

    const { loginRedirectReason, eduEmail, isEduAuthentication, isEduWithMobile } = config;
    if (eduEmail || isEduAuthentication) {
      dispatch(
        updateAuthProgress({
          isEmailAuthentication: true,
          emailAddress: eduEmail,
          isEduAuthentication,
          isEduWithMobile,
        }),
      );
    }

    if (config.isUserLoggedIn && isSignupPath(pathname)) {
      dispatch(enableKnownUserSignup());
    }

    const linkedOfferToken = couponToken || '';
    const linkedOfferErrorMessage = couponErrorMessage || '';
    const linkedOfferType = (couponToken && OfferType.COUPON) || '';
    const linkedOfferDetails = isEmpty(couponList) ? [] : couponList;

    batch(async () => {
      dispatch(
        updateBillingInfo({
          ...(terms && { offerTerms: terms }),
          ...(linkedOfferToken && {
            subscriptionCodeInput: linkedOfferToken,
            isLinkedOffer: !linkedOfferErrorMessage,
            showSubscriptionCodeField: true,
          }),
        }),
      );

      if (linkedOfferToken && !linkedOfferErrorMessage) {
        dispatch(
          setSharedBillingInfo({
            subscriptionCode: linkedOfferToken,
            subscriptionCodeValid: true,
            subscriptionCodeDetails: linkedOfferDetails,
            subscriptionCodeType: linkedOfferType,
            ...(termsUrl && {
              subscriptionCodeTermsUrl: termsUrl,
            }),
          }),
        );
        handleStartSignup();
        dispatch(flashSubscriptionCodeStatus());
      }

      if (linkedOfferToken && linkedOfferErrorMessage) {
        dispatch(setSubscriptionCodeValidationError(linkedOfferErrorMessage));
      }
    }).catch(CATCH_ERROR);

    if (linkedOfferErrorMessage && !loginRedirectReason && !linkedOfferType) {
      dispatch(showFlashNotice({ body: linkedOfferErrorMessage, type: NOTICE_ERROR_TYPE }));
      handleErrorDisplayedTrack({
        message: linkedOfferErrorMessage,
        errorType: ErrorType.MODAL,
        component: COMPONENT_FLASH_BANNER,
        canRetry: false,
      });
    }

    if (loginRedirectReason === OfferType.COUPON) {
      dispatch(setAuthMessage(COUPON_REDIRECT_MESSAGE));
    }

    const { addonPriceChangeDetails } = config;
    if (!isNil(addonPriceChangeDetails)) {
      const { announcementDate, effectiveDate, newPriceCents, productId } = addonPriceChangeDetails;

      dispatch(
        setAddOnPriceChangeConfig({
          priceChangeAnnouncementDate: announcementDate,
          priceChangePackageId: productId,
          priceChangeIncreasedPriceCents: newPriceCents,
          priceChangeEffectiveDate: effectiveDate,
        }),
      );
    }

    handleLoadUserGeolocation();
  }, []);
}
